import { ANALYTICS_EVENTS } from '../constants/analytics';
import { safeAnalytics } from './analytics';

/**
 * Session-storage key for this tab's one-shot persistence-reload claim.
 * Session, not local: a workspace switch reloads every tab at once, so each
 * tab that lost its read needs its own attempt, and the claim must not
 * outlive the tab.
 */
const AUTH_PERSISTENCE_RELOAD_KEY = 'bower.authPersistenceReload';

/**
 * Session-storage key holding a recovery phase that still needs SENDING.
 * Separate from the claim above, which records that a reload was SPENT.
 */
const AUTH_PERSISTENCE_PENDING_KEY = 'bower:pending_auth_persistence_recovery';

const RECOVERY_PHASES = ['reloading', 'exhausted'] as const;
type RecoveryPhase = (typeof RECOVERY_PHASES)[number];

function isRecoveryPhase(value: string): value is RecoveryPhase {
  return (RECOVERY_PHASES as readonly string[]).includes(value);
}

/** Drop the stash. Never throws: if storage is gone, so is the stash. */
function discardRecoveryStash(): void {
  try {
    sessionStorage.removeItem(AUTH_PERSISTENCE_PENDING_KEY);
  } catch {
    // Storage vanished; nothing left to discard.
  }
}

/**
 * Stash a recovery phase for delivery after analytics init, instead of sending
 * it here.
 *
 * Sending inline does not work, and looks like it does. This code runs during
 * Firebase's initial auth resolve — app boot — while `main.tsx` defers
 * `safeAnalytics.init()` to `requestIdleCallback` (2s fallback), and
 * `safeAnalytics.track()` returns at its `!isActive` gate with no pre-init
 * queue. main.tsx says so outright: "Events fired between app boot and idle
 * callback are intentionally skipped." On the `reloading` path the page is gone
 * a moment later as well, so even a queue would not have saved it.
 *
 * That mattered more than a dropped counter usually would: the docblock below
 * tells the next engineer to retire this backstop on the evidence of this
 * event, and evidence that never arrives is worse than none.
 *
 * Mirrors `utils/emailCtaCapture`, whose flush `initAnalytics` already calls
 * immediately after init for exactly this reason. sessionStorage, so it
 * survives the reload it is reporting and dies with the tab.
 *
 * `exhausted` deliberately overwrites a pending `reloading`: it means one
 * happened AND failed, which is the more informative of the two. So a tab that
 * never recovers reports once, not twice.
 */
function stashRecoveryPhase(phase: RecoveryPhase): void {
  try {
    sessionStorage.setItem(AUTH_PERSISTENCE_PENDING_KEY, phase);
  } catch {
    // Storage refused. The recovery itself matters more than reporting it, and
    // this must not throw into the caller's "never throws" contract.
  }
}

/**
 * Deliver a stashed recovery phase. Called through `flushPendingAnalyticsEvents`
 * (`analyticsConsentGrant.ts`) right after `safeAnalytics.init()` and on every
 * consent grant.
 *
 * Best-effort in exactly the way the event is documented to be: a tab that
 * denied analytics consent drops this like any other event. Clears first, so a
 * long-lived tab reports once rather than on every init.
 */
export function flushPendingAuthPersistenceRecovery(): void {
  let phase: string | null = null;
  try {
    phase = sessionStorage.getItem(AUTH_PERSISTENCE_PENDING_KEY);
  } catch {
    return;
  }
  if (!phase) return;

  // sessionStorage is client-writable and this value becomes an event
  // property, so treat it as input, not payload.
  if (!isRecoveryPhase(phase)) {
    discardRecoveryStash();
    return;
  }

  // Check BEFORE clearing. `track()` returns at its `!isActive` gate —
  // `isInitialized && (isProduction || isDebug) && !isFeatureFlagsOnly` — which
  // is exactly what isReady() reports, so clearing first would destroy the
  // report with no retry behind it: the same silent loss this telemetry exists
  // to report. emailCtaCapture, the model named above, likewise clears only
  // after a send it believes happened.
  //
  // This gate is not the ONLY way an event dies. `before_send` also drops on
  // host/APP_ENV, and an unset POSTHOG_KEY drops everything, so a debug build
  // or a Firebase preview channel can pass here and still send nothing. Those
  // are deliberately not guarded: each is fixed for the life of the document,
  // so a stash preserved against them would sit forever rather than be
  // retried. isFeatureFlagsOnly is the only drop that can flip while the tab
  // lives, and it is inside isActive — which is why this gate is the right one
  // rather than merely a cheap one.
  if (!safeAnalytics.isReady()) return;

  safeAnalytics.track(ANALYTICS_EVENTS.AUTH_PERSISTENCE_RECOVERY, { phase });
  discardRecoveryStash();
}

/**
 * Recover from Firebase resolving its persisted session to "no user" on a page
 * load where the user IS signed in.
 *
 * OBSERVED in production 2026-09-18. A workspace switch reloaded every other
 * tab at once (the cross-tab handler in App.tsx); one of those tabs came back
 * with `onAuthStateChanged` firing `null`, and AuthContext processed it as a
 * signout — painting the login page over a live session, dropping the
 * workspace, and resetting the analytics identity. The user clicked "Continue
 * with Google" and the popup could never complete, because nothing was
 * actually wrong with her session. It had NOT self-corrected 26 seconds later,
 * so a grace period would not have saved it; the tab recovered only when she
 * reloaded it five minutes on.
 *
 * CAUSE, confirmed 2026-09-30 and fixed in `config/firebase.ts`: the old
 * `getAuth()` + un-awaited `setPersistence()` moved the stored session between
 * IndexedDB and localStorage on every load, deleting it from one before the
 * other held it, so a sibling reloaded in the same storm could read both empty.
 * Auth now names its persistence at construction (`initializeAuth`), and this
 * recovery is only a backstop. Retire it on evidence, not on the fix alone,
 * and note the `AUTH_PERSISTENCE_RECOVERY` event this stashes UNDERCOUNTS badly:
 * production recorded 2 of them in the fortnight to 2026-09-30, while one user
 * alone re-entered her password 11 times on 2026-09-28. Why it goes missing is
 * not established; count re-logins (`User Signed In` shortly after `Workspace
 * Switched`) instead.
 *
 * Nothing re-reads persistence within a page load, so recovery IS one reload.
 * Returns true when one was started and the caller must stop processing.
 *
 * False means one of three things, all of which must fall through to the
 * normal signed-out path: no evidence this device holds a session (a genuine
 * first visit has to reach the login form with no added delay), this tab
 * already spent its attempt, or storage refused to record one.
 *
 * Never throws, and fails CLOSED. The claim IS the loop guard, so a reload we
 * cannot record is a reload that repeats forever. Declining strands the user
 * on a login page, which is wrong but fixable by hand; an infinite reload is
 * neither.
 *
 * Two scope asymmetries, both deliberate, neither free:
 *  - The claim is per-TAB (sessionStorage) because a workspace switch reloads
 *    every tab at once and each one needs its own attempt. The marker is
 *    per-DEVICE. So in that same storm, the first tab to exhaust its claim
 *    removes `workspaceId` (AuthContext's signout branch) and a sibling whose
 *    Firebase resolves a beat later finds no marker and gets no recovery.
 *    One page-load wide, and the 2026-09-18 incident was a single tab.
 *  - `!previousUserRef.current` does not mean "first fire": that ref is
 *    assigned `null` on a real signout too, so after one the tab sits inside
 *    this gate for the rest of its life. What stops a spurious reload on the
 *    login page is that the signout removed the marker — an invariant owned by
 *    AuthContext's signout branch, not by this function.
 */
export function attemptAuthPersistenceReload(): boolean {
  try {
    // `workspaceId` is the workspace pin WorkspaceContext writes once a
    // workspace resolves. Read it as the weakest claim that still does the
    // job: "a workspace was selected on this device and no in-app sign-out has
    // run since". It does NOT prove a live session — server-side revocation
    // leaves the pin untouched — and its absence does not prove a signout,
    // since AuthContext's sign-IN paths pre-clear it too. Being wrong in either
    // direction costs exactly one page load, which is what makes the weak
    // claim sufficient.
    if (!localStorage.getItem('workspaceId')) return false;
    if (sessionStorage.getItem(AUTH_PERSISTENCE_RELOAD_KEY)) {
      // Spent: this tab already reloaded once and STILL saw no user, so the
      // recovery did not work and a live-looking session is about to be torn
      // down. Report it — silence here is the one outcome nobody would ever
      // hear about (rule 12).
      stashRecoveryPhase('exhausted');
      // Nothing reloads after this, so if analytics is already up (the idle
      // callback beat Firebase's auth resolve) deliver now. Otherwise this
      // no-ops and initAnalytics' flush collects it.
      flushPendingAuthPersistenceRecovery();
      return false;
    }
    sessionStorage.setItem(AUTH_PERSISTENCE_RELOAD_KEY, '1');
  } catch {
    return false;
  }

  // Before the reload, never after: there is no page left afterwards. Stashed
  // rather than sent — see stashRecoveryPhase. Cannot throw, so it is safe out
  // here, outside the claim's try.
  stashRecoveryPhase('reloading');

  try {
    globalThis.location.reload();
  } catch {
    // Honor this function's "never throws" contract. A throw would escape
    // AuthContext's async auth-state listener, skip its `setBootLoading(false)`,
    // and strand EVERY route behind LoadingFallback — with the one-shot already
    // recorded as spent, so the next load could not retry either.
    //
    // Undo BOTH records, not just the claim: the reload did not happen, so a
    // surviving stash would have the next init report one that never occurred.
    try {
      sessionStorage.removeItem(AUTH_PERSISTENCE_RELOAD_KEY);
    } catch {
      // Storage vanished mid-call; there is nothing left to undo.
    }
    discardRecoveryStash();
    return false;
  }
  return true;
}

/**
 * Hand this tab its one-shot back after a successful auth resolve, so a later
 * stall in a long-lived tab can recover too. The intervening success is what
 * keeps that from being a loop. Never throws: if storage is gone, so is the
 * claim we would be releasing.
 */
export function releaseAuthPersistenceReloadClaim(): void {
  try {
    sessionStorage.removeItem(AUTH_PERSISTENCE_RELOAD_KEY);
  } catch {
    // Storage disabled — then no claim was ever recorded to release.
  }
}
